import React from 'react';
import styled from 'styled-components';

import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';

import { formatDuration, formatTime } from 'ff/debrief/utils';
import {
    useChrono,
    useCurrentDebrief,
    useCurrentTracklog,
    useDebriefApp,
    useAnalytics,
} from 'ff/debrief/hooks';

import TimelinePanel from 'ff/debrief/layouts/TimelinePanel';

import GraphIcon from 'ff/debrief/components/customIcons/GraphIcon';
import PlayPauseButton from 'ff/debrief/components/timeline/PlayPauseButton';
import SpeedSelectDropdown from 'ff/debrief/components/timeline/SpeedSelectDropdown';
import TimelineRail from 'ff/debrief/components/timeline/TimelineRail';
import { FFTheme } from 'ff/debrief/common/StyledComponentThemes';

const Timeline = () => {
    const { scrollSegmentIntoView, isChartOpen, toggleChartOpen } =
        useDebriefApp();
    const { trimmedValues } = useCurrentTracklog();
    const { debrief, hasDebrief } = useCurrentDebrief();
    const chrono = useChrono();
    const { logDataPaneToggled } = useAnalytics();

    const [wasTicking, setWasTicking] = React.useState(false);

    const handleScrub = (evt: Event, value: number | number[]) => {
        if (chrono.isTicking) {
            chrono.pause();
            setWasTicking(true);
        }
        if (typeof value === 'number') {
            chrono.setOffset(value);
        }
    };

    const handleThumbDrop = () => {
        if (hasDebrief) {
            scrollSegmentIntoView();
        }
        if (wasTicking) {
            chrono.play();
            setWasTicking(false);
        }
    };

    const handleChartButtonClick = () => {
        logDataPaneToggled({
            state: isChartOpen ? 'open' : 'closed',
        });
        toggleChartOpen();
    };

    const handleSpeedChange = (speed: number) => {
        chrono.setSpeed(speed);
    };

    const start = Math.floor(trimmedValues?.timestampStart || 0);
    const end = Math.floor(trimmedValues?.timestampEnd || 0);
    const totalTime = end - start;

    return (
        <TimelinePanel>
            <Stack direction="row" alignItems="center" px={2.5} py={2}>
                <PlayPauseButton
                    isPlaying={chrono.isTicking}
                    onPlayClick={chrono.play}
                    onPauseClick={chrono.pause}
                />
                <Stack
                    alignItems="center"
                    justifyContent="center"
                    minWidth="86px"
                    px={1.5}
                >
                    <CurrentTime>{formatTime(chrono.currentTime)}</CurrentTime>
                    <SpeedSelectDropdown
                        currentSpeed={chrono.speed}
                        onSpeedChange={handleSpeedChange}
                    />
                </Stack>
                <TimelineSlider
                    slotProps={{
                        rail: debrief
                            ? {
                                  // @ts-expect-error weird type stuff
                                  segments: debrief?.debriefSegments,
                                  totalTime: totalTime,
                              }
                            : undefined,
                    }}
                    slots={{ rail: debrief ? TimelineRail : undefined }}
                    value={chrono.offset}
                    onChange={handleScrub}
                    onChangeCommitted={handleThumbDrop}
                    min={0}
                    max={totalTime}
                    track={false}
                    valueLabelDisplay="on"
                    valueLabelFormat={v => formatDuration(v)}
                />
                <TotalDuration>{formatDuration(totalTime)}</TotalDuration>
                <ChartButton
                    color="primary"
                    value="chart"
                    selected={isChartOpen}
                    onChange={handleChartButtonClick}
                >
                    <GraphIcon />
                </ChartButton>
            </Stack>
        </TimelinePanel>
    );
};

const CurrentTime = styled(Typography)`
    font-size: 14px;
    font-weight: 500;
    text-transform: uppercase;
    white-space: nowrap;
`;

const TotalDuration = styled.span`
    color: #646468;
    text-align: right;
    font-size: 12px;
    font-weight: 400;
`;

const ChartButton = styled(ToggleButton)`
    min-width: auto;
    padding: 8px;
    margin-left: 8px;
    &.Mui-selected {
        background-color: ${FFTheme.blue};
        svg path {
            fill: white;
        }
    }
    &.Mui-selected:hover {
        background-color: ${FFTheme.blueHover};
    }
`;

const TimelineSlider = styled(Slider)(() => ({
    height: 5,
    marginLeft: 20,
    marginRight: 20,
    '& .MuiSlider-thumb': {
        height: 16,
        width: 16,
        backgroundColor: '#fff',
        transition: 'none',
        border: '2px solid #24374D',
        '&:focus, &:hover, &.Mui-active': {
            boxShadow: '0px 0px 3px 1px rgba(0, 0, 0, 0.1)',
            // Reset on touch devices, it doesn't add specificity
            '@media (hover: none)': {
                boxShadow: '',
            },
        },
    },
    '& .MuiSlider-valueLabel': {
        fontSize: 12,
        top: 2,
        backgroundColor: 'unset',
        '& *': {
            background: 'transparent',
            color: '#646468',
        },
    },
    '& .MuiSlider-rail': {
        opacity: 'unset',
        backgroundColor: '#293e52',
    },
    '& .MuiSlider-track': {
        transition: 'none',
    },
}));

export default Timeline;
